import { useEffect } from 'react'
import {
  PurchaseProductDetailsAddonIngredient,
  PurchaseProductDetailsAddonProduct,
  PurchaseProductDetailsAddonProductVariant,
} from '@ancon/wildcat-types'
import { getFormattedCurrency } from '@ancon/wildcat-utils/currency'
import { useController, useFormContext } from 'react-hook-form'
import useTranslation from 'next-translate/useTranslation'
import { SelectableType } from '@ancon/wildcat-ui/web/orderweb/Selectable'
import {
  ConfigureAddonGroupFormState,
  ConfigureAddonIngredientFormState,
  ConfigureAddonProductFormState,
} from '@ancon/wildcat-utils/product/types'
import isDetailedProductVariantOutOfStock from '@ancon/wildcat-utils/inventory/isDetailedProductVariantOutOfStocks'
import { getDetailedProductVariantStock } from '@ancon/wildcat-utils/inventory'
import clsx from 'clsx'

import QuantityModifier from '../../../app/components/QuantityModifier'
import BodyText from '../../../app/components/BodyText'
import ProductSelection from '../../components/ProductSelection'
import getPriceLabelForVariant from '../../utils/getPriceLabelForAddonProductVariant'
import { outletSelectedOutletIdSelector } from '../../../outlet/store/outletSelector'
import useAppSelector from '../../../../store/hooks/useAppSelector'

import styles from './QuantityModifierAddonGroupForm.module.scss'

type QuantityModifierAddonGroupProps = {
  hasBlockedProduct: boolean
  ingredients: PurchaseProductDetailsAddonIngredient[]
  addOnIndex: number
  products: PurchaseProductDetailsAddonProduct[]
}

function QuantityModifierAddonIngredient({
  ingredient,
  addOnIndex,
  ingredientIndex,
}: {
  ingredient: PurchaseProductDetailsAddonIngredient
  addOnIndex: number
  ingredientIndex: number
}) {
  const { t } = useTranslation('common')
  const { trigger, setValue, watch, getValues } = useFormContext()

  const addOnSelectedCountFieldName = `selectedAddons.${addOnIndex}.selectedCount`
  const addOnSettingsFieldName = `selectedAddons.${addOnIndex}.addOnSettings`

  const base = `selectedAddons.${addOnIndex}.ingredients.${ingredientIndex}`
  const selectedCountFieldName = `${base}.selectedCount`
  const freeAmountFieldName = `${base}.freeAmount`
  const addOnIngredientsFieldName = `selectedAddons.${addOnIndex}.ingredients`

  const [selectedAddOnCount, addOnSettings] = watch([
    addOnSelectedCountFieldName,
    addOnSettingsFieldName,
  ])

  const hasFreeAddons = !!addOnSettings.freeAmount
  const isWithinFreeLimit =
    hasFreeAddons && addOnSettings.freeAmount! > selectedAddOnCount

  const {
    field: { value: countValue = 0, onChange: onCountChange },
  } = useController({ name: selectedCountFieldName, defaultValue: 0 })

  function handleQuantityChange(quantity: number, isIncrement: boolean) {
    onCountChange(quantity)

    const currentAddOnSelectedCount = getValues(addOnSelectedCountFieldName)
    setValue(
      addOnSelectedCountFieldName,
      Math.max(
        isIncrement
          ? currentAddOnSelectedCount + 1
          : currentAddOnSelectedCount - 1,
        0,
      ),
    )

    // Modify free addon state - Start
    const [currentFreeAmount, currentSelectedCount] = getValues([
      freeAmountFieldName,
      selectedCountFieldName,
    ])

    // On increase add as free addOn
    if (isIncrement && isWithinFreeLimit) {
      setValue(freeAmountFieldName, currentFreeAmount + 1)
      trigger(`selectedAddons.${addOnIndex}`)
      return
    }

    // On decrease free addon assign free item to next candidate addOn item
    const hasFreeAdditions = currentFreeAmount > 0
    if (
      !isIncrement &&
      hasFreeAdditions &&
      currentSelectedCount < currentFreeAmount
    ) {
      setValue(freeAmountFieldName, currentFreeAmount - 1)

      const ingredientSelectionState: ConfigureAddonIngredientFormState[] =
        getValues(addOnIngredientsFieldName)
      const nextFreeCandidateIngredientIndex =
        ingredientSelectionState.findIndex(
          ing => ing.id !== ingredient.id && ing.freeAmount < ing.selectedCount,
        )

      if (nextFreeCandidateIngredientIndex !== -1) {
        const candidateFreeField = `selectedAddons.${addOnIndex}.ingredients.${nextFreeCandidateIngredientIndex}.freeAmount`
        const candidateCurrentFreeAmount = getValues(candidateFreeField) || 0
        setValue(candidateFreeField, candidateCurrentFreeAmount + 1)
      }
    }
    // Modify free addon state - End

    // Trigger Validation Cycle
    trigger(`selectedAddons.${addOnIndex}`)
  }

  const max: number | null =
    addOnSettings.maximumAmount &&
    selectedAddOnCount >= addOnSettings.maximumAmount
      ? countValue
      : addOnSettings.maximumAmount ?? null

  const { ingredientName, price, currency } = ingredient

  return (
    <div className={styles.container}>
      <BodyText>{ingredientName}</BodyText>
      <div className={styles.leftContainer}>
        <QuantityModifier
          className={styles.quantityModifier}
          initialValue={countValue}
          min={0}
          max={max || undefined}
          onChange={handleQuantityChange}
        />
        <BodyText className={styles.priceText}>
          {isWithinFreeLimit
            ? t('components.productDetailsModal.free')
            : getFormattedCurrency(
                hasFreeAddons ? addOnSettings.afterFreePrice.amount : price,
                currency,
              )}
        </BodyText>
      </div>
    </div>
  )
}

function QuantityModifierAddonProduct({
  hasBlockedProduct,
  product,
  addOnIndex,
  productIndex,
}: {
  hasBlockedProduct: boolean
  product: PurchaseProductDetailsAddonProduct
  addOnIndex: number
  productIndex: number
}) {
  const { setValue, getValues, trigger, watch, control } = useFormContext()
  const { t } = useTranslation('common')
  const outletId = useAppSelector(outletSelectedOutletIdSelector)

  const addOnSelectedCountFieldName = `selectedAddons.${addOnIndex}.selectedCount`
  const addOnSettingsFieldName = `selectedAddons.${addOnIndex}.addOnSettings`
  const addOnProductsFieldName = `selectedAddons.${addOnIndex}.products`

  const [selectedAddOnCount, addOnSettings] = watch([
    addOnSelectedCountFieldName,
    addOnSettingsFieldName,
  ])

  const hasFreeAddons = !!addOnSettings.freeAmount
  const isWithinFreeLimit =
    hasFreeAddons && addOnSettings.freeAmount! > selectedAddOnCount

  const base = `selectedAddons.${addOnIndex}.products.${productIndex}`
  const selectedCountFieldName = `${base}.selectedCount`
  const freeAmountFieldName = `${base}.freeAmount`

  // Quantity (controlled)
  const {
    field: { value: countValue = 0, onChange: onCountChange },
  } = useController({ name: selectedCountFieldName, control, defaultValue: 0 })

  function isVariantStockAvailable(
    variant: PurchaseProductDetailsAddonProductVariant,
  ) {
    const variantStock = getDetailedProductVariantStock(variant, outletId!)

    if (variantStock?.stockProductId) {
      return (variantStock.stockCount ?? 0) > 0
    }

    return true
  }

  function getDefaultSelectedVariantId() {
    const stockAvailableVariant = product.variants
      .slice()
      .sort((a, b) => a.position - b.position)
      .find(isVariantStockAvailable)

    if (stockAvailableVariant) {
      return stockAvailableVariant.id
    }

    return product.variants[0]?.id ?? null
  }

  // Variant (persisted in form)
  const {
    field: { value: selectedVariantId, onChange: onVariantChange },
  } = useController({
    name: `${base}.selectedVariantId`,
    control,
    defaultValue: getDefaultSelectedVariantId(),
  })

  // Ensure a default variant is set
  useEffect(() => {
    if (!selectedVariantId && product.hasVariants && product.variants?.length) {
      onVariantChange(product.variants[0].id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariantId, product])

  function handleQuantityChange(quantity: number, isIncrement: boolean) {
    onCountChange(quantity)

    const currentAddOnSelectedCount = getValues(addOnSelectedCountFieldName)

    setValue(
      addOnSelectedCountFieldName,
      Math.max(
        isIncrement
          ? currentAddOnSelectedCount + 1
          : currentAddOnSelectedCount - 1,
        0,
      ),
      { shouldDirty: true, shouldValidate: true },
    )

    // Free-addons logic (products)
    const [currentFreeAmount, currentSelectedCount] = getValues([
      freeAmountFieldName,
      selectedCountFieldName,
    ])

    if (isIncrement && isWithinFreeLimit) {
      setValue(freeAmountFieldName, (currentFreeAmount || 0) + 1, {
        shouldDirty: true,
        shouldValidate: true,
      })
      trigger(`selectedAddons.${addOnIndex}`)
      return
    }

    const hasFreeAdditions = (currentFreeAmount || 0) > 0
    if (
      !isIncrement &&
      hasFreeAdditions &&
      currentSelectedCount < currentFreeAmount
    ) {
      setValue(freeAmountFieldName, currentFreeAmount - 1, {
        shouldDirty: true,
        shouldValidate: true,
      })

      const productSelectionState =
        (getValues(
          addOnProductsFieldName,
        ) as ConfigureAddonProductFormState[]) || []

      const nextFreeCandidateProductIndex = productSelectionState.findIndex(
        p =>
          p.id !== product.id && (p.freeAmount || 0) < (p.selectedCount || 0),
      )

      if (nextFreeCandidateProductIndex !== -1) {
        const candidateFreeField = `selectedAddons.${addOnIndex}.products.${nextFreeCandidateProductIndex}.freeAmount`
        const candidateCurrentFreeAmount = getValues(candidateFreeField) || 0
        setValue(candidateFreeField, candidateCurrentFreeAmount + 1, {
          shouldDirty: true,
          shouldValidate: true,
        })
      }
    }

    trigger(`selectedAddons.${addOnIndex}`)
  }

  const selectedVariant = product.variants?.find(
    v => v.id === selectedVariantId,
  )

  // Clamp quantity when variant changes (cannot exceed new variant's stock)
  useEffect(() => {
    if (!selectedVariant) return
    const stock = getDetailedProductVariantStock(
      selectedVariant,
      outletId!,
    )?.stockCount

    if (typeof stock !== 'number') return

    const clamped = Math.max(0, stock)
    if (countValue > clamped) {
      const stockDiff = countValue - clamped

      // 1 Clamp this product/variant count in RHF
      setValue(selectedCountFieldName, clamped, {
        shouldDirty: true,
        shouldValidate: true,
      })

      // 2 Decrement the add-on’s overall selected count
      const currentAddOnSelectedCount =
        getValues(addOnSelectedCountFieldName) || 0

      setValue(
        addOnSelectedCountFieldName,
        Math.max(currentAddOnSelectedCount - stockDiff, 0),
        { shouldDirty: true, shouldValidate: true },
      )

      // 3 Adjust freeAmount if it now exceeds the clamped count
      const currentFreeAmount = getValues(freeAmountFieldName) || 0
      if (currentFreeAmount > clamped) {
        setValue(freeAmountFieldName, clamped, {
          shouldDirty: true,
          shouldValidate: true,
        })
      }

      trigger(`selectedAddons.${addOnIndex}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariantId, outletId, countValue])

  const isSelectedVariantOutOfStock = selectedVariant
    ? isDetailedProductVariantOutOfStock(selectedVariant, outletId!)
    : false

  const productPriceLabel = getPriceLabelForVariant(
    selectedVariant!,
    isWithinFreeLimit,
    hasFreeAddons,
    addOnSettings.afterFreePrice.amount,
    product.isBlocked,
    isSelectedVariantOutOfStock,
    t,
  )

  // Product is out of stock only if ALL variants are out of stock
  function isProductOutOfStock() {
    if (product.hasVariants && product.variants?.length) {
      return product.variants.every(variant =>
        isDetailedProductVariantOutOfStock(variant, outletId!),
      )
    }
    return false
  }

  const outOfStock = isProductOutOfStock()

  function getMaximumAllowedQuantity() {
    const { maximumAmount } = addOnSettings

    // Maximum allowed by add-on settings
    const max: number | null =
      !!maximumAmount && selectedAddOnCount >= maximumAmount
        ? countValue
        : maximumAmount ?? null

    if (selectedVariant) {
      const variantStock = getDetailedProductVariantStock(
        selectedVariant,
        outletId!,
      )

      const stock = variantStock?.stockCount ?? null

      // If inventory defined
      if (stock !== null) {
        const currentStock = Math.max(0, stock)

        if (max !== null) {
          return Math.min(max, currentStock)
        }

        return currentStock
      }
    }

    return max ?? undefined
  }

  const maximumAllowedQuantity = getMaximumAllowedQuantity()

  return (
    <>
      <div className={styles.container}>
        <BodyText
          className={
            outOfStock || product.isBlocked ? styles.disabled : undefined
          }
        >
          {product.name}
          {outOfStock && (
            <> - {t('components.productDetailsModal.variantOutOfStock')}</>
          )}
        </BodyText>
        <div className={styles.leftContainer}>
          <QuantityModifier
            className={styles.quantityModifier}
            value={countValue}
            min={0}
            max={maximumAllowedQuantity}
            disabled={outOfStock || product.isBlocked}
            onChange={handleQuantityChange}
          />
          <BodyText
            className={clsx(styles.priceText, {
              [styles.priceTextWide]: hasBlockedProduct,
              [styles.priceTextDisabled]: product.isBlocked,
            })}
          >
            {productPriceLabel}
          </BodyText>
        </div>
      </div>

      {product.hasVariants && !!product.variants?.length && (
        <div>
          {countValue > 0 &&
            product.variants.map(
              (variant: PurchaseProductDetailsAddonProductVariant) => {
                const isOutOfStock = isDetailedProductVariantOutOfStock(
                  variant,
                  outletId!,
                )
                const variantPriceLabel = getPriceLabelForVariant(
                  variant,
                  isWithinFreeLimit,
                  hasFreeAddons,
                  addOnSettings.afterFreePrice.amount,
                  product.isBlocked,
                  isOutOfStock,
                  t,
                )

                return (
                  <div className={styles.variantContainer} key={variant.id}>
                    <ProductSelection
                      label={variant.name}
                      priceLabel={variantPriceLabel}
                      disabled={isOutOfStock || product.isBlocked}
                      selectionType={SelectableType.RadioButton}
                      selected={selectedVariantId === variant.id}
                      onChange={() => onVariantChange(variant.id)}
                    />
                  </div>
                )
              },
            )}
        </div>
      )}
    </>
  )
}

export default function QuantityModifierAddonGroup({
  hasBlockedProduct,
  ingredients,
  addOnIndex,
  products,
}: QuantityModifierAddonGroupProps) {
  const { t } = useTranslation('common')
  const { trigger } = useFormContext()

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const field = useController({
    name: `selectedAddons.${addOnIndex}`,
    rules: {
      validate: (value: ConfigureAddonGroupFormState) => {
        const { selectedCount: totalAddonCount, addOnSettings } = value
        const { maximumAmount, minimumAmount } = addOnSettings

        if (minimumAmount && totalAddonCount === 0) {
          return t('components.productDetailsModal.mandatoryChoice').toString()
        }
        if (minimumAmount && totalAddonCount < minimumAmount) {
          return t('components.productDetailsModal.atLeastAddons', {
            minimum: minimumAmount,
          }).toString()
        }
        if (maximumAmount && totalAddonCount > maximumAmount) {
          return t('components.productDetailsModal.maxAddons', {
            maximum: maximumAmount,
          }).toString()
        }
        return true
      },
    },
  })

  useEffect(() => {
    // Trigger Initial Validation Cycle
    trigger(`selectedAddons.${addOnIndex}`)
  }, [trigger, addOnIndex])

  return (
    <>
      {ingredients.map((ingredient, i) => (
        <QuantityModifierAddonIngredient
          key={`ingredient-${ingredient.id}`}
          ingredient={ingredient}
          addOnIndex={addOnIndex}
          ingredientIndex={i}
        />
      ))}

      {products.map((product, i) => (
        <QuantityModifierAddonProduct
          key={`product-${product.id}`}
          hasBlockedProduct={hasBlockedProduct}
          product={product}
          addOnIndex={addOnIndex}
          productIndex={i}
        />
      ))}
    </>
  )
}
